import { c as _c } from "react/compiler-runtime";
import { type AccrualPosition, MathLib } from '@morpho-org/blue-sdk';
import { ZERO } from '@morpho-org/dapp-core';
import { useMemo } from 'react';
import { computeLtv } from '@/hooks/operation/market/v2/marketAction.helpers';

/** Current on-chain state of one refinance leg + its oracle price. */
export interface RefinancePositionSnapshot {
  collateral: bigint;
  borrowAssets: bigint;
  price: bigint;
}

/** A position as the recap displays it (before or after), with its WAD LTV. */
export interface RefinancePositionView {
  collateral: bigint;
  borrowAssets: bigint;
  ltv: bigint;
}

/**
 * The pure JS-math from/to position preview both refinance steps share: current
 * snapshots (collateral / debt / oracle price) of the two accrual positions, the
 * initial recap views (zero-defaulted while loading, display-gated by the callers),
 * and the plain-delta positions-after — source zero-floored, target additive.
 *
 * The input step ({@link RefinanceDialog}) displays the after views directly (no
 * evm-simulation there); the review step ({@link RefinanceReviewDialog}) feeds them
 * to `useMarketRefinanceSimulatedFinalPosition` as the fallback shown until the
 * simulation verifies the real outcome. Keeping the math here guarantees the two
 * steps can never preview different positions for the same inputs.
 */
export function useRefinancePositionPreview(t0) {
  const $ = _c(35);
  const {
    sourcePositionData,
    targetPositionData,
    collateralAmount,
    movedDebt
  } = t0;
  let t1;
  if ($[0] !== sourcePositionData) {
    t1 = sourcePositionData ? {
      collateral: sourcePositionData.collateral,
      borrowAssets: sourcePositionData.borrowAssets,
      price: sourcePositionData.market.price ?? ZERO
    } : undefined;
    $[0] = sourcePositionData;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sourceCurrent = t1;
  let t2;
  if ($[2] !== targetPositionData) {
    t2 = targetPositionData ? {
      collateral: targetPositionData.collateral,
      borrowAssets: targetPositionData.borrowAssets,
      price: targetPositionData.market.price ?? ZERO
    } : undefined;
    $[2] = targetPositionData;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const targetCurrent = t2;
  let t3;
  if ($[4] !== sourceCurrent) {
    t3 = toInitialView(sourceCurrent);
    $[4] = sourceCurrent;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const sourceInitial = t3;
  let t4;
  if ($[6] !== targetCurrent) {
    t4 = toInitialView(targetCurrent);
    $[6] = targetCurrent;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const targetInitial = t4;
  let t5;
  bb0: {
    if (!sourceCurrent) {
      t5 = undefined;
      break bb0;
    }
    let t6;
    let t7;
    let t8;
    if ($[8] !== collateralAmount || $[9] !== movedDebt || $[10] !== sourceCurrent.borrowAssets || $[11] !== sourceCurrent.collateral || $[12] !== sourceCurrent.price) {
      const collateral = MathLib.zeroFloorSub(sourceCurrent.collateral, collateralAmount);
      const borrowAssets = MathLib.zeroFloorSub(sourceCurrent.borrowAssets, movedDebt);
      t6 = collateral;
      t7 = borrowAssets;
      t8 = computeLtv({
        collateral,
        borrowAssets,
        price: sourceCurrent.price
      });
      $[8] = collateralAmount;
      $[9] = movedDebt;
      $[10] = sourceCurrent.borrowAssets;
      $[11] = sourceCurrent.collateral;
      $[12] = sourceCurrent.price;
      $[13] = t6;
      $[14] = t7;
      $[15] = t8;
    } else {
      t6 = $[13];
      t7 = $[14];
      t8 = $[15];
    }
    let t9;
    if ($[16] !== t6 || $[17] !== t7 || $[18] !== t8) {
      t9 = {
        collateral: t6,
        borrowAssets: t7,
        ltv: t8
      };
      $[16] = t6;
      $[17] = t7;
      $[18] = t8;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    t5 = t9;
  }
  const sourceAfterJs = t5;
  let t6;
  bb1: {
    if (!targetCurrent) {
      t6 = undefined;
      break bb1;
    }
    const collateral_0 = targetCurrent.collateral + collateralAmount;
    const borrowAssets_0 = targetCurrent.borrowAssets + movedDebt;
    let t7;
    if ($[20] !== borrowAssets_0 || $[21] !== collateral_0 || $[22] !== targetCurrent.price) {
      t7 = computeLtv({
        collateral: collateral_0,
        borrowAssets: borrowAssets_0,
        price: targetCurrent.price
      });
      $[20] = borrowAssets_0;
      $[21] = collateral_0;
      $[22] = targetCurrent.price;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    let t8;
    if ($[24] !== borrowAssets_0 || $[25] !== collateral_0 || $[26] !== t7) {
      t8 = {
        collateral: collateral_0,
        borrowAssets: borrowAssets_0,
        ltv: t7
      };
      $[24] = borrowAssets_0;
      $[25] = collateral_0;
      $[26] = t7;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    t6 = t8;
  }
  const targetAfterJs = t6;
  let t7;
  if ($[28] !== sourceAfterJs || $[29] !== sourceCurrent || $[30] !== sourceInitial || $[31] !== targetAfterJs || $[32] !== targetCurrent || $[33] !== targetInitial) {
    t7 = {
      sourceCurrent,
      targetCurrent,
      sourceInitial,
      targetInitial,
      sourceAfterJs,
      targetAfterJs
    };
    $[28] = sourceAfterJs;
    $[29] = sourceCurrent;
    $[30] = sourceInitial;
    $[31] = targetAfterJs;
    $[32] = targetCurrent;
    $[33] = targetInitial;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  return t7;
}
const toInitialView = (current: RefinancePositionSnapshot | undefined): RefinancePositionView => {
  const collateral = current?.collateral ?? 0n;
  const borrowAssets = current?.borrowAssets ?? 0n;
  return {
    collateral,
    borrowAssets,
    ltv: computeLtv({
      collateral,
      borrowAssets,
      price: current?.price ?? 0n
    })
  };
};